import { useCallback, useEffect, useState } from 'react'

type Breakpoint =
  | 'mobile_small'
  | 'mobile'
  | 'tablet'
  | 'tablet_landscape'
  | 'desktop'
  | 'desktop_large'

enum BreakpointsEnum {
  mobile_small = '279px',
  mobile = '639px',
  tablet = '767px',
  tablet_landscape = '1023px',
  desktop = '1365px',
  desktop_large = '1535px'
}

type DeviceType = 'short-desktop-height'

interface Conditions {
  maxWidth?: Breakpoint
  device?: DeviceType
}

interface WidthConditions extends Conditions {
  maxWidth: Breakpoint
  device?: never
}

type HeightConditions = {
  maxWidth?: never
  device: DeviceType
}

type UseMediaQueryProps = WidthConditions | HeightConditions

/**
 * Custom hook that listens for changes in media queries and returns a boolean indicating whether the conditions have been met.
 *
 * This hook supports two types of conditions:
 * 1. Width-based conditions: This is determined by a maximum width threshold.
 * 2. Height-based conditions: This is determined by a specific device type for the height of the screen.
 *
 * @param {UseMediaQueryProps} conditions - The conditions that need to be matched for the query to return `true`.
 *
 * @returns {boolean} - `true` if the media query matches the given conditions, `false` otherwise.
 *
 * @example
 * // Example for width-based condition
 * const isMobile = useMediaQuery({ maxWidth: 'mobile' });
 *
 * // Example for height-based condition
 * const isShortDesktop = useMediaQuery({ device: 'short-desktop-height' }); *
 *
 */
export const useMediaQuery = (conditions: UseMediaQueryProps) => {
  const [targetReached, setTargetReached] = useState<boolean>()

  const updateTarget = useCallback((e: MediaQueryListEvent) => {
    if (e.matches) setTargetReached(true)
    else setTargetReached(false)
  }, [])

  useEffect(() => {
    const isHeightCondition = !!conditions.device

    let media: MediaQueryList | null = null

    if (isHeightCondition && conditions.device === 'short-desktop-height') {
      media = window.matchMedia('(max-height: 700px) and (min-width: 1024px)')
      media.addEventListener('change', updateTarget)
    } else {
      media = window.matchMedia(
        `(max-width: ${BreakpointsEnum[conditions.maxWidth]})`
      )
      media.addEventListener('change', updateTarget)
    }

    setTargetReached(media.matches)

    return () => {
      if (media) media.removeEventListener('change', updateTarget)
    }
  }, [conditions, updateTarget])

  return targetReached
}
